import { TZDate } from '@date-fns/tz';

import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from './date';

/**
 * The zone the business books time in.
 *
 * Clock times are payroll input: a shift is 08:00 in Oslo whoever reads it.
 * Formatting the raw instant in the viewer's zone shows a manager abroad
 * different times than the worker who filed them and than the backend that
 * priced them, and a decision taken on those numbers is wrong by the offset.
 *
 * Defined here, once, because the worker app and the partner app format the same
 * clock values for the two sides of a correction; two copies drift. It mirrors
 * the backend's single `OSLO` anchor (`apps.time_tracking` pins the anchor date,
 * daily/weekly tallies and payroll grouping to it), so "08:00" here and "08:00"
 * there are the same instant wherever the phone is. All three sides must change
 * together.
 */
export const BUSINESS_TIME_ZONE = 'Europe/Oslo';

/** The same instant, read in `timeZone`. */
const inTimeZone = (value: string | number | Date, timeZone: string): TZDate =>
  new TZDate(value instanceof Date ? value : new Date(value), timeZone);

/** The same instant, read in the business zone. */
export const businessTime = (value: string | number | Date): TZDate => inTimeZone(value, BUSINESS_TIME_ZONE);

/**
 * Format an instant on the clock of the place it happens at: the service department's (or facility's)
 * IANA zone, locale-aware like `noddiFormatDate`.
 *
 * Use this -- not `noddiFormatDate` -- for every clock time of an instant (`HH:mm`, date with time): a
 * pickup, a delivery window, a shift. `noddiFormatDate` reads the viewer's zone, so a dispatcher
 * abroad, or a laptop on UTC, sees a different time than the facility is told, and a time typed back
 * from it is saved wrong by the offset. Null/undefined format like `noddiFormatDate` does.
 */
export const formatInTimeZone = (
  value: string | number | Date | null | undefined,
  format: CustomDateFormats | NoddiDateFormats,
  timeZone: string
): string => noddiFormatDate(value == null ? value : inTimeZone(value, timeZone), format);

/**
 * Format an instant in the business zone, locale-aware like `noddiFormatDate`.
 *
 * Use this — not `noddiFormatDate` — for anything a worker and a manager must
 * read as the same clock: clock-in/out, proposed corrections, shift days.
 */
export const formatBusinessTime = (
  value: string | number | Date,
  format: CustomDateFormats | NoddiDateFormats
): string => formatInTimeZone(value, format, BUSINESS_TIME_ZONE);

/** `HH:mm–HH:mm` in the business zone: a window a customer was given, read the same on every phone. */
export const formatBusinessTimeRange = (start: string | number | Date, end: string | number | Date): string =>
  `${formatBusinessTime(start, NoddiDateFormats.TIME)}–${formatBusinessTime(end, NoddiDateFormats.TIME)}`;

/**
 * A wall-clock time on the calendar day `ymd`, as an instant in `timeZone`.
 *
 * Building the string and handing it to `new TZDate('2026-08-24T17:00:00',
 * timeZone)` does NOT do this. An ISO string
 * carrying no offset is parsed as *system* local time per the ECMAScript spec,
 * and `timeZone` then only labels it for display -- so the instant is wrong by
 * the gap between the two zones on every device outside `timeZone`, and right on
 * an Oslo laptop. Only the numeric constructor reads the parts as `timeZone`'s
 * own clock.
 *
 * Takes the time already parsed, so validating user text stays the caller's job
 * and this cannot fail for a reason a caller has to handle. `ymd` must be
 * `YYYY-MM-DD` -- every caller gets it from `noddiFormatDate(..., API_DATE)`.
 *
 * Returns a UTC `Z` string, so no caller has to know that `TZDate#toISOString`
 * keeps the offset form instead.
 */
export const zonedWallClockIso = (
  ymd: string,
  { hours, minutes }: { hours: number; minutes: number },
  timeZone: string
): string => {
  const [year, month, day] = ymd.split('-').map(Number);
  const wallClock = new TZDate(Number(year), Number(month) - 1, Number(day), hours, minutes, 0, timeZone);
  return new Date(wallClock.getTime()).toISOString();
};

/** `HH:MM` (24-hour) as `{ hours, minutes }`, or null if it is not a real reading. */
export const parseHhmm = (text: string): { hours: number; minutes: number } | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours > 23 || minutes > 59 ? null : { hours, minutes };
};
